import { useState, useEffect, useRef } from 'react';
import { Dashboard } from './components/Dashboard';
import { SettingsPage } from './components/SettingsPage';
import { saveSettings, getSettings } from './lib/calendar';
import './output.css';

type View = 'dashboard' | 'settings';

const REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes in milliseconds

export function App() {
  const [currentView, setCurrentView] = useState<View>('dashboard');
  const refreshTimerRef = useRef<number | null>(null);

  const resetRefreshTimer = () => {
    // Clear existing timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }

    // Set new timer
    refreshTimerRef.current = window.setTimeout(() => {
      window.location.reload();
    }, REFRESH_INTERVAL);
  };

  useEffect(() => {
    // Check for settings in URL parameters and save to localStorage
    const params = new URLSearchParams(window.location.search);
    const roomName = params.get('roomName');
    const googleCalendarEnabled = params.get('googleCalendarEnabled');
    const googleResourceEmail = params.get('googleResourceEmail');
    const refreshInterval = params.get('refreshInterval');

    if (roomName || googleResourceEmail) {
      const currentSettings = getSettings();
      const newSettings = {
        ...currentSettings,
        ...(roomName && { roomName }),
        ...(googleCalendarEnabled && { googleCalendarEnabled: googleCalendarEnabled === 'true' }),
        ...(googleResourceEmail && { googleResourceEmail }),
        ...(refreshInterval && { refreshInterval: parseInt(refreshInterval, 10) }),
      };
      saveSettings(newSettings);

      // Remove query parameters from URL without reload
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }

    // Start the initial timer
    resetRefreshTimer();

    // List of events that indicate user interaction
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click', 'focus'];

    // Add event listeners to reset timer on any interaction
    events.forEach((event) => {
      document.addEventListener(event, resetRefreshTimer, true);
    });

    // Cleanup
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
      events.forEach((event) => {
        document.removeEventListener(event, resetRefreshTimer, true);
      });
    };
  }, []);

  return (
    <>
      {currentView === 'dashboard' && (
        <Dashboard onOpenSettings={() => setCurrentView('settings')} />
      )}
      {currentView === 'settings' && <SettingsPage onBack={() => setCurrentView('dashboard')} />}
    </>
  );
}

export default App;
